import { supabase } from './supabase';

const BUCKET = 'audio';

interface UploadResult {
  storagePath: string;
  publicUrl: string | null;
}

/**
 * Upload a recorded audio blob to Supabase Storage.
 * Path: audio/{userId}/{noteId}/recording.webm
 */
export async function uploadAudio(
  userId: string,
  noteId: string,
  blob: Blob
): Promise<UploadResult> {
  const ext = extensionFromMime(blob.type);
  const storagePath = `${userId}/${noteId}/recording.${ext}`;

  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(storagePath, blob, {
      contentType: blob.type,
      upsert: true,
    });

  if (error) throw new Error(`Audio upload failed: ${error.message}`);

  // Generate a signed URL (valid 1 hour — for playback)
  const { data: urlData } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(storagePath, 3600);

  return {
    storagePath,
    publicUrl: urlData?.signedUrl ?? null,
  };
}

/**
 * Delete audio files for a note from Supabase Storage.
 */
export async function deleteAudio(
  userId: string,
  noteId: string
): Promise<void> {
  const { data: files } = await supabase.storage
    .from(BUCKET)
    .list(`${userId}/${noteId}`);

  if (files && files.length > 0) {
    const paths = files.map((f) => `${userId}/${noteId}/${f.name}`);
    await supabase.storage.from(BUCKET).remove(paths);
  }
}

function extensionFromMime(mime: string): string {
  if (mime.includes('webm')) return 'webm';
  if (mime.includes('mp4')) return 'mp4';
  if (mime.includes('ogg')) return 'ogg';
  return 'wav';
}
